import { useMemo, useRef, useState, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { CButton, CButtonGroup, CCard } from "@coreui/react-pro";
import type { TrackerResultsDto } from "../../../api/hooks/usePortfolioTracker";
import {
  chartFrame,
  formatDay,
  formatMoney,
  formatPercent,
  lineBounds,
  profitTo,
  stackProfits,
  valueAt,
  type ChartFrame,
  type DatedValue,
} from "../portfolio-tracker";
import { useElementSize } from "../useElementSize";
import { ChartTip, DrawdownZone, SteppedLine } from "./chart-parts";
import { FallStrip } from "./FallStrip";
import { StackedArea } from "./StackedArea";

type View = "total" | "strategy";

interface ValueChartProps {
  results: TrackerResultsDto;
  asOf: string;
  colours: Map<string, string>;
  /** The day the pointer is on, or null for today. */
  hoverDay: string | null;
  onHover: (day: string | null) => void;
}

/**
 * The portfolio's value over time: as one line, or as what was put in with each strategy's profit stacked on
 * it. Under it, how far below its last high it was. Moving along either picks the day Profit by strategy shows.
 */
export function ValueChart({ results, asOf, colours, hoverDay, onHover }: ValueChartProps) {
  const { t } = useTranslation("portfolioTracker");
  const [view, setView] = useState<View>("strategy");
  const hostRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(hostRef);

  const balance = useMemo<DatedValue[]>(() => results.balance.map(({ date, value }) => ({ date, value })), [results]);
  const fall = useMemo<DatedValue[]>(
    () => results.fallFromHigh.map(({ date, percent }) => ({ date, value: percent })),
    [results],
  );

  const stack = useMemo(() => {
    const dates = balance.map((point) => point.date);
    const profits = results.strategies.map((strategy) => ({
      id: strategy.tradingStrategyId,
      values: dates.map((day) => profitTo(strategy, day)),
    }));
    return stackProfits(dates, results.putIn, balance, profits);
  }, [balance, results]);

  const width = Math.max(260, size.width);
  const height = Math.max(120, size.height);
  const firstDay = balance[0]?.date ?? asOf;

  const frame: ChartFrame = useMemo(() => {
    if (view === "strategy") return chartFrame(width, height, 0, stack.top * 1.06, firstDay, asOf);
    const [low, high] = lineBounds(balance, results.putIn);
    return chartFrame(width, height, low, high, firstDay, asOf);
  }, [view, width, height, stack.top, firstDay, asOf, balance, results.putIn]);

  const pointerDay = (event: PointerEvent<HTMLDivElement>) =>
    frame.dayAt(event.clientX - event.currentTarget.getBoundingClientRect().left);

  const handleMove = (event: PointerEvent<HTMLDivElement>) => onHover(pointerDay(event));
  // A finger lifting is not leaving, so on a phone the day tapped stays until another is.
  const handleLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse") onHover(null);
  };

  const hoverValue = hoverDay ? valueAt(balance, hoverDay) : 0;
  const hoverFall = hoverDay ? -valueAt(fall, hoverDay) : 0;
  const zone = (
    <DrawdownZone
      frame={frame}
      from={results.drawdown.from}
      to={results.drawdown.to}
      percent={results.drawdown.percent}
    />
  );

  return (
    <CCard className="tracker-chart-card">
      <header className="tracker-chart-head">
        <h3 className="h6 mb-0">{t("chart.title")}</h3>
        <CButtonGroup size="sm" className="btn-group-pill" role="group" aria-label={t("chart.view")}>
          {(["total", "strategy"] as const).map((option) => (
            <CButton
              key={option}
              color="primary"
              variant={view === option ? undefined : "outline"}
              aria-pressed={view === option}
              onClick={() => setView(option)}
            >
              {t(`chart.${option}`)}
            </CButton>
          ))}
        </CButtonGroup>
      </header>

      <div className="tracker-legend">
        {view === "strategy" ? (
          <>
            <span>
              <i className="tracker-key-base-band" />
              {t("chart.keyPutIn")}
            </span>
            <span>
              <i className="tracker-key-bands" />
              {t("chart.keyBands")}
            </span>
            <span>
              <i className="tracker-key-total" />
              {t("chart.keyPortfolio")}
            </span>
          </>
        ) : (
          <>
            <span>
              <i className="tracker-key-line" />
              {t("chart.keyPortfolio")}
            </span>
            <span>
              <i className="tracker-key-base" />
              {t("chart.keyPutIn")}
            </span>
          </>
        )}
        <span>
          <i className="tracker-key-fall" />
          {t("chart.keyFall")}
        </span>
      </div>

      <div
        className="tracker-chart-wrap"
        onPointerMove={handleMove}
        onPointerDown={handleMove}
        onPointerLeave={handleLeave}
      >
        <div ref={hostRef} className="tracker-chart">
          {size.width > 0 && balance.length > 0 && (
            <svg
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-label={t(view === "strategy" ? "chart.labelStrategy" : "chart.labelTotal", {
                from: formatDay(firstDay),
                worth: formatMoney(results.worth),
              })}
            >
              {view === "strategy" ? (
                <StackedArea
                  frame={frame}
                  putIn={results.putIn}
                  balance={balance}
                  stack={stack}
                  colours={colours}
                  zone={zone}
                  hoverDay={hoverDay}
                  hoverValue={hoverValue}
                />
              ) : (
                <SteppedLine
                  frame={frame}
                  points={balance}
                  base={results.putIn}
                  endLabel={formatMoney(results.worth)}
                  zone={zone}
                  hoverDay={hoverDay}
                  hoverValue={hoverValue}
                />
              )}
            </svg>
          )}
          {hoverDay && size.width > 0 && (
            <ChartTip frame={frame} x={frame.x(hoverDay)}>
              <b>{formatMoney(hoverValue)}</b>
              <span>{formatDay(hoverDay)}</span>
              {hoverFall >= 0.05 && <span>{t("chart.belowHigh", { percent: formatPercent(hoverFall) })}</span>}
            </ChartTip>
          )}
        </div>
        {size.width > 0 && fall.length > 0 && (
          <FallStrip frame={frame} fall={fall} percent={results.drawdown.percent} hoverDay={hoverDay} />
        )}
      </div>

      <p className="tracker-chart-note mb-0">
        {view === "strategy" ? t("chart.noteStrategy") : t("chart.noteTotal")} {t("chart.noteFall")}
      </p>
    </CCard>
  );
}
